import { computed, ref, watch, watchEffect } from 'vue'

import type { GetOfferV1Response } from '@backmarket/http-api/src/api-specs-buyback/customer/getOfferV1'
import type { Question } from '@backmarket/http-api/src/api-specs-buyback/customer/getQuestionsV3'
import { useBuybackOffer } from '@backmarket/nuxt-layer-buyback/app/scopes/offer/composables/useBuybackOffer/useBuybackOffer'
import type { BuybackProduct } from '@backmarket/nuxt-layer-buyback/app/scopes/offer/types'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { removeEmptyValuesInObject } from '@backmarket/utils/object/removeEmptyValuesInObject'
import { closeModal } from '@ds/components/ModalBase'

import { useFetchOffer } from '~/scopes/buyback/composables/useFetchOffer'
import {
  type FunnelForm,
  useFunnelQuestions,
} from '~/scopes/buyback/composables/useFunnelQuestions'
import translations from '~/scopes/buyback/swap/components/SwapModal/SwapModal.translations'
import {
  STEPS,
  SWAP_INFO_MESSAGE,
} from '~/scopes/buyback/swap/components/SwapModal/constants'
import type { SwapModalProps } from '~/scopes/buyback/swap/components/SwapModalContainer/SwapModalContainer.vue'
import { useSwapModalStore } from '~/scopes/buyback/swap/stores/useSwapModalStore'
import { CHECKOUT } from '~/scopes/checkout/routes-names'

type Steps = (typeof STEPS)[keyof typeof STEPS]

type SwapModalEmit = (event: 'confirmation' | 'continue') => void

export function useSwapModal(props: SwapModalProps, emit: SwapModalEmit) {
  const i18n = useI18n()
  const logger = useLogger()
  const $httpFetch = useHttpFetch()
  const { trackClick, trackSwapOffer } = useTracking()

  const { setShowAddToCartModal } = useSwapModalStore()
  const {
    saveBuybackOffer,
    hasBuybackOffer,
    deleteBuybackOffer,
    saveHasNoOffer,
  } = useBuybackOffer()

  const getInitialStep = (): Exclude<Steps, 'no_offer'> => {
    if (!isEmpty(props.initialPayload)) {
      return STEPS.QUESTIONS
    }
    if (!props.intro) {
      return STEPS.CATEGORIES
    }

    return STEPS.INTRO
  }

  const step = ref<Exclude<Steps, 'no_offer'>>(getInitialStep())
  const categories = ref<Question | Record<string, never>>({})
  const isLoadingCategories = ref(false)
  const isLoadingInitialPayload = ref(false)
  const isOpen = ref(false)
  const offer = ref<GetOfferV1Response>()

  const swapEstimation = () =>
    parseInt(offer.value?.listing.price.amount || '0', 10)

  // Deferred dispatcher: wired up after useFetchOffer returns, but only
  // called during user interaction (never during setup), so it is always
  // assigned before first use.
  let dispatchFetchOffer: (payload: FunnelForm) => void = () => undefined

  const {
    activeStep: activeQuestionsStep,
    activeQuestions,
    funnel,
    form,
    isStepComplete,
    isLastQuestion,
    hasSubmitButton,
    buttonLabel,
    fetchError,
    fetchQuestionsWithPayload,
    handleAnswer,
    goToPreviousStep,
  } = useFunnelQuestions({
    kind: 'swap',
    onStepChange: (funnelStep, formPayload) => {
      trackClick({
        value: {
          ...formPayload,
          swap_estimation: swapEstimation(),
        },
        name: funnelStep.key,
        zone: props.zone,
      })
    },
    onReadyForOffer: (formPayload) => {
      dispatchFetchOffer(formPayload)
    },
    onUnprocessableEntity: () => {
      step.value = STEPS.INTRO
    },
  })

  function handleTracking(label: string) {
    trackClick({
      value: {
        ...props.initialPayload,
        ...form.value,
        swap_estimation: swapEstimation(),
      },
      name: label,
      zone: props.zone,
    })
  }

  function handleTrackingBack() {
    trackClick({
      value: {
        ...form.value,
        swap_estimation: swapEstimation(),
        active_step: activeQuestionsStep.value?.key || '',
      },
      name: 'funnel_arrow_back',
      zone: props.zone,
    })
  }

  function handleTrackingSwapOffer() {
    trackSwapOffer({
      ...form.value,
      swap_estimation: swapEstimation(),
      listing_public_id: offer.value?.listing.listingPublicId || '',
      zone: props.zone,
    })
  }

  const { isLoading: isPostingAnswers, fetchOffer } = useFetchOffer({
    kind: 'swap',
    minDelay: 800,
    onSuccess: async (offerValue, formPayload) => {
      offer.value = offerValue
      step.value = STEPS.OFFER

      try {
        await saveBuybackOffer(offerValue, formPayload)
        setShowAddToCartModal(false)
        handleTrackingSwapOffer()
        emit('confirmation')
      } catch (err) {
        logger.error(SWAP_INFO_MESSAGE.ADD_SWAP, {
          error: err as Error,
          owners: ['bot-squad-circularity-offer-front'],
        })
      }
    },
    onNoOffer: () => {
      saveHasNoOffer()
      step.value = STEPS.OFFER
      handleTracking('scarabee')
    },
    onError: (error) => {
      saveHasNoOffer()
      step.value = STEPS.OFFER
      logger.error(SWAP_INFO_MESSAGE.GET_OFFER, {
        httpError: error,
        owners: ['bot-squad-circularity-offer-front'],
      })
    },
  })

  dispatchFetchOffer = fetchOffer

  const model = computed(() => {
    if (!form.value?.model || Array.isArray(form.value.model)) {
      return ''
    }

    return form.value.model
  })

  const title = computed(() => {
    if (step.value === STEPS.QUESTIONS) {
      return activeQuestionsStep.value?.label ?? ''
    }

    return i18n(translations.offerTitle)
  })

  const closeWording = computed(() => {
    if (props.redirectOnCloseTarget !== null) {
      return i18n(translations.noToCart)
    }

    return i18n(translations.continue)
  })

  const displayLoader = computed(() => {
    return (
      isLoadingCategories.value ||
      isPostingAnswers.value ||
      isLoadingInitialPayload.value
    )
  })

  const loadingText = computed(() => {
    return isPostingAnswers.value ? i18n(translations.loadingOfferText) : ''
  })

  const displayIntro = computed(() => {
    return step.value === STEPS.INTRO && !isLoadingCategories.value
  })

  const displayCategories = computed(() => {
    return step.value === STEPS.CATEGORIES && !isLoadingCategories.value
  })

  const displayFunnel = computed(() => {
    return (
      step.value === STEPS.QUESTIONS &&
      !isPostingAnswers.value &&
      !isLoadingCategories.value
    )
  })

  const displayOffer = computed(() => {
    return step.value === STEPS.OFFER && !isLoadingCategories.value
  })

  async function fetchCategoriesFromApi(): Promise<Question | null> {
    const response = await $httpFetch(
      '/buyback-funnel/api/v1/funnel/:kind/questions',
      {
        pathParams: { kind: 'swap' },
        query: { embedded: 'true' },
      },
    )

    const firstStepQuestions = response?.funnel?.[0]?.questions || []

    return firstStepQuestions[0] ?? null
  }

  async function getCategories(): Promise<void> {
    handleTracking('buyback_estimate')

    isLoadingCategories.value = true

    try {
      const categoriesFromApi = await fetchCategoriesFromApi()

      if (categoriesFromApi) {
        categories.value = categoriesFromApi
      }
      step.value = STEPS.CATEGORIES
    } catch (err) {
      logger.error(SWAP_INFO_MESSAGE.GET_CATEGORIES, {
        error: err as Error,
        owners: ['bot-squad-circularity-offer-front'],
      })
    } finally {
      isLoadingCategories.value = false
    }
  }

  async function getCategoryFunnel(selectedCategory: { category: string }) {
    try {
      form.value = { ...selectedCategory }
      handleTracking('category')

      await fetchQuestionsWithPayload({
        ...form.value,
      })

      if (isLastQuestion.value && isStepComplete.value) {
        const preSelectedValues: FunnelForm = {}
        activeQuestions.value.forEach((q) => {
          const { value } = q
          if (value !== false && value !== '') {
            preSelectedValues[q.key] = value
          }
        })
        form.value = { ...selectedCategory, ...preSelectedValues }
        dispatchFetchOffer(form.value)
      } else {
        step.value = STEPS.QUESTIONS
      }
    } catch (err) {
      logger.error(SWAP_INFO_MESSAGE.GET_QUESTIONS, {
        error: err as Error,
        owners: ['bot-squad-circularity-offer-front'],
      })
    }
  }

  async function goToCategories() {
    if (isEmpty(categories.value)) {
      await getCategories()
    }
    step.value = STEPS.CATEGORIES
  }

  async function getFunnelStepFromHistory(): Promise<void> {
    const isFirstQuestionStep =
      funnel.value.findIndex(
        ({ step: questionFunnelStep }) => questionFunnelStep?.active,
      ) === 0

    if (isFirstQuestionStep) {
      await goToCategories()

      return
    }

    try {
      await goToPreviousStep()
    } catch (err) {
      logger.error(SWAP_INFO_MESSAGE.GET_QUESTIONS, {
        error: err as Error,
        owners: ['bot-squad-circularity-offer-front'],
      })
    }
  }

  async function handleBack(): Promise<void> {
    handleTrackingBack()

    switch (step.value) {
      case STEPS.CATEGORIES:
        step.value = STEPS.INTRO
        break
      case STEPS.QUESTIONS:
        await getFunnelStepFromHistory()
        break
      case STEPS.OFFER:
        step.value = STEPS.QUESTIONS
        break
      default:
        break
    }
  }

  function handleCloseModal() {
    closeModal(props.modalName)
  }

  function resetFunnel(): void {
    funnel.value = []
    form.value = {}
    offer.value = undefined
    categories.value = {}
    step.value = STEPS.INTRO
  }

  function handleClose() {
    if (step.value === STEPS.INTRO && props.intro === 'atc') {
      handleTracking('go_to_cart')
    } else {
      handleTracking(
        hasBuybackOffer.value
          ? 'close_swap_offer_modal'
          : 'close_swap_funnel_modal',
      )
    }

    isOpen.value = false
    resetFunnel()

    if (props.redirectOnCloseTarget === CHECKOUT.CART) {
      emit('continue')
    }
  }

  function handleOpen() {
    isOpen.value = true
  }

  function handleRemove() {
    handleTracking('remove_swap')
    deleteBuybackOffer()
    handleCloseModal()
  }

  async function handleItemSelected(item: BuybackProduct) {
    resetFunnel()
    step.value = STEPS.QUESTIONS

    const initialValue: FunnelForm = removeEmptyValuesInObject({
      category: item.category,
      brand: item.brand,
      model: item.model,
      model_family: item.productLine,
    })

    isLoadingInitialPayload.value = true

    try {
      await fetchQuestionsWithPayload({
        ...initialValue,
        embedded: 'true',
        nextStep: 'true',
      })
    } catch (err) {
      logger.error(SWAP_INFO_MESSAGE.GET_QUESTIONS, {
        error: err as Error,
        owners: ['bot-squad-circularity-offer-front'],
      })
    } finally {
      isLoadingInitialPayload.value = false
    }
  }

  watch(fetchError, (error) => {
    if (!error) return

    logger.error(SWAP_INFO_MESSAGE.GET_QUESTIONS, {
      error,
      owners: ['bot-squad-circularity-offer-front'],
    })
  })

  watch(
    [() => props.initialPayload, () => props.intro, isOpen],
    async ([newInitialPayload, newIntro, newIsOpen]) => {
      if (newIsOpen) {
        step.value = getInitialStep()
        handleTracking('swap')

        if (!isEmpty(newInitialPayload)) {
          isLoadingInitialPayload.value = true

          try {
            await fetchQuestionsWithPayload({
              ...newInitialPayload,
              embedded: 'true',
              nextStep: 'true',
            })
          } finally {
            isLoadingInitialPayload.value = false
          }

          return
        }

        if (!newIntro) {
          await getCategories()
        }
      }
    },
    { immediate: true },
  )

  watchEffect(() => {
    if (step.value && isOpen.value) {
      logger.info(SWAP_INFO_MESSAGE.VIEW_SWAP_STEP, {
        step: step.value,
        owners: ['bot-squad-circularity-offer-front'],
      })
    }
  })

  return {
    // State
    step,
    categories,
    offer,
    isPostingAnswers,
    isLoadingCategories,
    isLoadingInitialPayload,
    // Computed display
    displayLoader,
    displayIntro,
    displayCategories,
    displayFunnel,
    displayOffer,
    loadingText,
    title,
    closeWording,
    buttonLabel,
    model,
    // From useFunnelQuestions
    activeQuestions,
    isStepComplete,
    hasSubmitButton,
    form,
    // Handlers
    handleBack,
    handleClose,
    handleOpen,
    handleCloseModal,
    handleRemove,
    handleItemSelected,
    getCategoryFunnel,
    getCategories,
    goToCategories,
    onAnswer: handleAnswer,
  }
}
